import { memo } from "react";
import { CircularProgress } from "@mui/material";
import { truncate } from "lodash";
import { UserNotFound } from "@zenith/assets/icons/components";
import { InitialsSquareComponent } from "@zenith/components";
import { sortedBeneficiaryDetailsRecord } from "@zenith/pages/BeneficiariesPage/beneficiaries-page/beneficiaries-page";
import Check from "@zenith/assets/icons/Check";
import CloseCircle from "@zenith/assets/icons/CloseCircle";

interface BeneficiaryListProps {
  isLoading: boolean;
  hasError: boolean;
  beneficiaries?: sortedBeneficiaryDetailsRecord[];
  selectedBeneficiary?: sortedBeneficiaryDetailsRecord;
  onSelect: (beneficiary: sortedBeneficiaryDetailsRecord) => void;
}

const BeneficiaryList = memo(function BeneficiaryList({
  isLoading,
  hasError,
  beneficiaries,
  selectedBeneficiary,
  onSelect,
}: BeneficiaryListProps) {
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <CircularProgress className="text-primary-300" size="4rem" />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <CloseCircle width={24} height={24} fill="#DA291C" />
        <p className="mt-2 text-center font-medium text-neutral-900">
          Something Went Wrong
        </p>
        <p className="w-56 text-center text-sm text-neutral-600">
          We could not load beneficiaries list. Please try again.
        </p>
      </div>
    );
  }

  if (!beneficiaries || beneficiaries.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-150">
          <UserNotFound />
        </div>
        <p className="mt-2 text-center font-medium text-neutral-900">
          No Beneficiaries Found
        </p>
        <p className="w-60 text-center text-sm text-neutral-600">
          It seems there are no beneficiaries to display at the moment.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pb-1">
      {beneficiaries.map((beneficiary, idx) => (
        <div
          className="mb-0.5 flex cursor-pointer items-center rounded-lg py-3 pl-1 pr-3 hover:bg-neutral-25"
          key={idx}
          onClick={() => onSelect(beneficiary)}
        >
          <InitialsSquareComponent
            displayName={beneficiary.displayName}
            size="sm"
          />
          <div className="flex items-center">
            <div>
              <p className="text-neutral-600">
                {truncate(beneficiary.displayName, { length: 25 })}
              </p>
              <p className="text-sm text-neutral-600">
                {beneficiary.accountNumber} - {beneficiary.bankName}
              </p>
            </div>
            <span className="ml-4">
              {selectedBeneficiary?.accountNumber ===
                beneficiary.accountNumber && <Check />}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
});

export default BeneficiaryList;
